import { displayName, type Person } from '@lineage/core';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { shareLink } from '../../lib/native';
import { Modal } from '../../components/Modal';
import { useToast } from '../../components/Toast';
import { api } from '../../lib/api';
import { useT } from '../../i18n';

interface Props {
  treeId: string;
  treeName: string;
  person: Person | null;
  isOwner: boolean;
  onClose: () => void;
}

/** Creates an invite link and hands it to the native share sheet (WhatsApp is the expected path). */
export function InviteDialog({ treeId, treeName, person, isOwner, onClose }: Props) {
  const t = useT().invite;
  const linkCopied = useT().common.linkCopied;
  const [role, setRole] = useState<'viewer' | 'editor'>('viewer');
  const [email, setEmail] = useState('');
  const [maxUses, setMaxUses] = useState(person ? 1 : 50);
  const toast = useToast((s) => s.show);
  const create = useMutation({
    mutationFn: () =>
      api<{ link: string }>('POST', `/trees/${treeId}/invites`, {
        role,
        personId: person?.id ?? null,
        email: email.trim() || null,
        maxUses: email.trim() ? 1 : maxUses,
      }),
  });
  const link = create.data?.link;
  const message = t.message(treeName);
  const share = async () => {
    if (!link) return;
    const r = await shareLink({ title: treeName, text: message, url: link });
    if (r === 'copied') toast(linkCopied);
  };
  return (
    <Modal title={person ? t.titlePerson(displayName(person)) : t.titleAnyone} onClose={onClose}>
      {!link ? (
        <form
          className="stack"
          onSubmit={(e) => {
            e.preventDefault();
            create.mutate();
          }}
        >
          <p className="muted">{person ? t.explainPerson(displayName(person)) : t.explainAnyone}</p>
          {isOwner && (
            <div className="field">
              <span>{t.role}</span>
              <div className="segmented">
                <button type="button" aria-pressed={role === 'viewer'} onClick={() => setRole('viewer')}>{t.viewer}</button>
                <button type="button" aria-pressed={role === 'editor'} onClick={() => setRole('editor')}>{t.editor}</button>
              </div>
            </div>
          )}
          <label className="field">
            <span>{t.onlyEmail}</span>
            <input className="input" type="email" inputMode="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t.onlyEmailHint} />
          </label>
          {!email.trim() && (
            <label className="field">
              <span>{t.maxUses}</span>
              <input className="input" type="number" min={1} max={50} value={maxUses} onChange={(e) => setMaxUses(Math.min(50, Math.max(1, Number(e.target.value) || 1)))} />
            </label>
          )}
          {create.error && <div className="error">{(create.error as Error).message}</div>}
          <button className="btn btn-primary" disabled={create.isPending}>{t.createLink}</button>
        </form>
      ) : (
        <div className="stack">
          <input className="input" readOnly value={link} onFocus={(e) => e.currentTarget.select()} aria-label={t.link} />
          <button className="btn btn-primary" onClick={share}>{t.share}</button>
          <button className="btn" onClick={async () => { await navigator.clipboard?.writeText(link); toast(linkCopied); }}>{t.copy}</button>
        </div>
      )}
    </Modal>
  );
}
